import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    X, FileText, Presentation, Loader2, Sparkles, Download, Globe,
    EyeOff, AlertTriangle, CheckCircle2, Link2, Eye, Ban,
} from "lucide-react";

/* PitchModal — generate a "Perfect Match" one-pager (PDF) and/or pitch deck
   (PPTX) for a match, rendered in the CLIENT's branding (auto-detected from
   their website, with manual accent override). */
export const PitchModal = ({ match, onClose }) => {
    const { t } = useTranslation();
    const [formats, setFormats] = useState({ onepager: true, deck: true });
    const [language, setLanguage] = useState("nl");
    const [anonymous, setAnonymous] = useState(false);
    const [website, setWebsite] = useState("");
    const [branding, setBranding] = useState(null);
    const [detecting, setDetecting] = useState(false);
    const [accentOverride, setAccentOverride] = useState("");
    const [generating, setGenerating] = useState(false);
    const [pitches, setPitches] = useState([]);
    const pollRef = useRef(null);

    const loadPitches = async () => {
        try {
            const r = await api.get(`/matches/${match.id}/pitch`);
            setPitches(r.data || []);
        } catch { /* noop */ }
    };
    useEffect(() => { loadPitches(); return () => clearTimeout(pollRef.current); }, []);

    const detect = async () => {
        if (!website.trim()) return;
        setDetecting(true);
        try {
            const r = await api.post("/pitch/branding-detect", { website: website.trim() });
            setBranding(r.data);
            if (!r.data.logo_url) toast.info(t("pitch.branding_not_found"));
        } catch { toast.error(t("pitch.branding_failed")); }
        finally { setDetecting(false); }
    };

    const generate = async () => {
        const fmts = Object.entries(formats).filter(([, v]) => v).map(([k]) => k);
        if (fmts.length === 0) { toast.warning(t("pitch.pick_format")); return; }
        setGenerating(true);
        try {
            const r = await api.post(`/matches/${match.id}/pitch`, {
                formats: fmts, language, anonymous,
                client_website: website.trim() || null,
                accent_color: accentOverride || null,
            });
            toast.info(t("pitch.generating_toast"));
            pollUntilReady(r.data.pitch_id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("pitch.generate_failed"));
            setGenerating(false);
        }
    };

    const pollUntilReady = (pitchId, attempt = 0) => {
        pollRef.current = setTimeout(async () => {
            try {
                const r = await api.get(`/pitch/${pitchId}`);
                if (r.data.status === "ready") {
                    setGenerating(false);
                    toast.success(t("pitch.ready_toast"));
                    loadPitches();
                    return;
                }
                if (r.data.status === "failed") {
                    setGenerating(false);
                    toast.error(r.data.error || t("pitch.generate_failed"));
                    loadPitches();
                    return;
                }
            } catch { /* transient */ }
            if (attempt < 60) pollUntilReady(pitchId, attempt + 1);
            else { setGenerating(false); toast.error(t("pitch.timeout")); }
        }, 3000);
    };

    const download = async (pitch, fmt) => {
        try {
            const r = await api.get(`/pitch/${pitch.id}/file/${fmt}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `match_${(pitch.candidate_name || "kandidaat").replace(/\s+/g, "_")}.${fmt}`;
            a.click(); URL.revokeObjectURL(url);
        } catch { toast.error(t("pitch.download_failed")); }
    };

    const copyShareUrl = async (token) => {
        const url = `${window.location.origin}/p/${token}`;
        try { await navigator.clipboard.writeText(url); }
        catch {
            const ta = document.createElement("textarea");
            ta.value = url; document.body.appendChild(ta);
            ta.select(); document.execCommand("copy"); ta.remove();
        }
        toast.success(t("pitch.share_copied"));
    };

    const share = async (pitch) => {
        try {
            const r = await api.post(`/pitch/${pitch.id}/share`);
            await copyShareUrl(r.data.share_token);
            loadPitches();
        } catch (e) { toast.error(e?.response?.data?.detail || t("pitch.share_failed")); }
    };

    const revokeShare = async (pitch) => {
        try {
            await api.delete(`/pitch/${pitch.id}/share`);
            toast.success(t("pitch.share_revoked"));
            loadPitches();
        } catch (e) { toast.error(e?.response?.data?.detail || t("pitch.share_failed")); }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={onClose} data-testid="pitch-modal">
            <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-[var(--jr-surface)] p-6"
                onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-brand)]">{t("pitch.kicker")}</div>
                        <h3 className="font-display text-xl text-white mt-1">{match.candidate_name} → {match.company_name}</h3>
                        <p className="text-xs text-[var(--jr-text-3)] mt-1">{t("pitch.subtitle")}</p>
                    </div>
                    <button onClick={onClose} data-testid="pitch-modal-close" className="p-1 text-[var(--jr-text-3)] hover:text-white"><X size={18}/></button>
                </div>

                {/* Formats */}
                <div className="mt-5">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2">{t("pitch.formats")}</div>
                    <div className="flex gap-2">
                        <button onClick={() => setFormats((f) => ({ ...f, onepager: !f.onepager }))} data-testid="pitch-fmt-onepager"
                            className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-xs transition-colors ${formats.onepager ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/10 text-white" : "border-white/10 text-[var(--jr-text-3)] hover:text-white"}`}>
                            <FileText size={14}/> {t("pitch.fmt_onepager")}
                        </button>
                        <button onClick={() => setFormats((f) => ({ ...f, deck: !f.deck }))} data-testid="pitch-fmt-deck"
                            className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-xs transition-colors ${formats.deck ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/10 text-white" : "border-white/10 text-[var(--jr-text-3)] hover:text-white"}`}>
                            <Presentation size={14}/> {t("pitch.fmt_deck")}
                        </button>
                    </div>
                </div>

                {/* Language + anonymous */}
                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-1.5">{t("pitch.language")}</div>
                        <div className="flex gap-1">
                            {["nl", "en", "fr"].map((l) => (
                                <button key={l} onClick={() => setLanguage(l)} data-testid={`pitch-lang-${l}`}
                                    className={`text-[10px] font-mono uppercase px-2.5 py-1.5 rounded-full border transition-colors ${language === l ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/15 text-white" : "border-white/10 text-[var(--jr-text-3)] hover:text-white"}`}>
                                    {l}
                                </button>
                            ))}
                        </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="pitch-anonymous-toggle">
                        <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="accent-[var(--jr-brand)]"/>
                        <span className="text-xs text-[var(--jr-text-2)] inline-flex items-center gap-1"><EyeOff size={11}/> {t("pitch.anonymous")}</span>
                    </label>
                </div>

                {/* Client branding */}
                <div className="mt-4">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-1.5">{t("pitch.client_branding")}</div>
                    <div className="flex items-center gap-1.5">
                        <input value={website} onChange={(e) => setWebsite(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && detect()}
                            placeholder={t("pitch.website_placeholder")} data-testid="pitch-website-input"
                            className="flex-1 min-w-0 bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-[var(--jr-brand)]"/>
                        <button onClick={detect} disabled={detecting || !website.trim()} data-testid="pitch-detect-branding"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[var(--jr-info)]/40 text-[var(--jr-info)] text-xs hover:bg-[var(--jr-info)]/10 disabled:opacity-50 shrink-0">
                            {detecting ? <Loader2 size={12} className="animate-spin"/> : <Globe size={12}/>} {t("pitch.detect")}
                        </button>
                    </div>
                    {branding && (
                        <div className="mt-2 flex items-center gap-2 rounded-lg border border-white/8 bg-[var(--jr-surface-2)]/40 px-3 py-2" data-testid="pitch-branding-preview">
                            {branding.logo_url && <img src={branding.logo_url} alt="" className="w-7 h-7 rounded object-contain bg-white/90 p-0.5"/>}
                            <div className="flex gap-1">
                                {(branding.colors || []).map((c, i) => (
                                    <span key={i} className="w-5 h-5 rounded-full border border-white/20" style={{ backgroundColor: c }} title={c}/>
                                ))}
                            </div>
                            <span className="text-[10px] font-mono text-[var(--jr-text-3)]">{branding.domain}</span>
                        </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                        <span className="text-[10px] text-[var(--jr-text-3)]">{t("pitch.accent_override")}</span>
                        <input type="color" value={accentOverride || branding?.accent || "#FF5A00"}
                            onChange={(e) => setAccentOverride(e.target.value)} data-testid="pitch-accent-input"
                            className="w-7 h-7 rounded cursor-pointer bg-transparent border border-white/10"/>
                        {accentOverride && (
                            <button onClick={() => setAccentOverride("")} className="text-[10px] text-[var(--jr-text-3)] hover:text-white underline">{t("common.reset", "reset")}</button>
                        )}
                    </div>
                </div>

                <button onClick={generate} disabled={generating} data-testid="pitch-generate-btn"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                    {generating ? <Loader2 size={15} className="animate-spin"/> : <Sparkles size={15}/>}
                    {generating ? t("pitch.generating") : t("pitch.generate")}
                </button>

                {/* Previous generations */}
                {pitches.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-white/8">
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2">{t("pitch.previous")}</div>
                        <ul className="space-y-1.5">
                            {pitches.map((p) => (
                                <li key={p.id} className="flex items-center gap-2 rounded-lg border border-white/8 bg-[var(--jr-surface-2)]/40 px-3 py-2 text-xs"
                                    data-testid={`pitch-row-${p.id}`}>
                                    {p.status === "ready" ? <CheckCircle2 size={12} className="text-[#22c55e] shrink-0"/>
                                        : p.status === "failed" ? <AlertTriangle size={12} className="text-[var(--jr-error)] shrink-0"/>
                                        : <Loader2 size={12} className="animate-spin text-[var(--jr-amber)] shrink-0"/>}
                                    <span className="flex items-center gap-1 shrink-0" data-testid={`pitch-fmt-labels-${p.id}`}>
                                        {(p.formats || []).includes("onepager") && (
                                            <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.1em] px-1.5 py-0.5 rounded bg-[var(--jr-brand)]/12 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30">
                                                <FileText size={9}/> {t("pitch.label_onepager")}
                                            </span>
                                        )}
                                        {(p.formats || []).includes("deck") && (
                                            <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.1em] px-1.5 py-0.5 rounded bg-[#a855f7]/12 text-[#a855f7] border border-[#a855f7]/30">
                                                <Presentation size={9}/> {t("pitch.label_deck")}
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-[var(--jr-text-2)] truncate flex-1">
                                        {new Date(p.created_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                        {" · "}{p.language?.toUpperCase()}{p.anonymous ? ` · ${t("pitch.anon_short")}` : ""}
                                        {p.branding?.domain ? ` · ${p.branding.domain}` : ""}
                                    </span>
                                    {p.status === "ready" && p.pdf_path && (
                                        <button onClick={() => download(p, "pdf")} data-testid={`pitch-dl-pdf-${p.id}`}
                                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                                            <Download size={10}/> PDF
                                        </button>
                                    )}
                                    {p.status === "ready" && p.pptx_path && (
                                        <button onClick={() => download(p, "pptx")} data-testid={`pitch-dl-pptx-${p.id}`}
                                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                                            <Download size={10}/> PPT
                                        </button>
                                    )}
                                    {p.status === "ready" && (
                                        p.share_enabled ? (
                                            <span className="inline-flex items-center gap-1 shrink-0">
                                                <span className="inline-flex items-center gap-1 px-1.5 py-1 rounded bg-[#22c55e]/12 text-[#22c55e] border border-[#22c55e]/30 text-[10px] font-mono"
                                                    title={p.last_opened_at ? t("pitch.last_opened", { when: new Date(p.last_opened_at).toLocaleString() }) : t("pitch.not_opened_yet")}
                                                    data-testid={`pitch-opens-${p.id}`}>
                                                    <Eye size={10}/> {p.open_count || 0}×
                                                </span>
                                                <button onClick={() => copyShareUrl(p.share_token)} data-testid={`pitch-copy-link-${p.id}`}
                                                    title={t("pitch.copy_link")}
                                                    className="p-1 rounded border border-white/10 text-[var(--jr-info)] hover:text-white hover:bg-white/5">
                                                    <Link2 size={11}/>
                                                </button>
                                                <button onClick={() => revokeShare(p)} data-testid={`pitch-revoke-${p.id}`}
                                                    title={t("pitch.share_revoke")}
                                                    className="p-1 rounded border border-white/10 text-[var(--jr-text-3)] hover:text-[var(--jr-error)] hover:bg-white/5">
                                                    <Ban size={11}/>
                                                </button>
                                            </span>
                                        ) : (
                                            <button onClick={() => share(p)} data-testid={`pitch-share-${p.id}`}
                                                title={t("pitch.share")}
                                                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[var(--jr-info)]/40 text-[var(--jr-info)] hover:bg-[var(--jr-info)]/10">
                                                <Link2 size={10}/> {t("pitch.share_short")}
                                            </button>
                                        )
                                    )}
                                    {p.status === "failed" && <span className="text-[10px] text-[var(--jr-error)] truncate max-w-[140px]" title={p.error}>{p.error}</span>}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};
